/**
 * The filename a media blob travels under (FEAT-028 wave 1).
 *
 * Two upload paths hardcoded a `.webm` extension for every video, from the days
 * when every recording really was WebM. Now that the recorder prefers MP4, the
 * name has to follow the bytes: the extension is the only hint some tooling
 * gets, and a file called `video.webm` that is really an MP4 is the kind of
 * small lie that costs an afternoon later.
 *
 * The server never trusts this — it validates magic bytes and probes the real
 * streams — but honest names cost nothing.
 */

const VIDEO_EXTENSIONS: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/x-m4v': 'm4v',
  'video/webm': 'webm',
};

const PHOTO_EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/**
 * `uploadFileName(blob, 'video')` → `video.mp4` / `video.webm` / …
 * Falls back to the sensible default for the type when the blob has no usable
 * MIME (some browsers hand back an empty `type`).
 */
export function uploadFileName(
  blob: Blob,
  mediaType: 'photo' | 'video',
  stem = mediaType === 'video' ? 'video' : 'photo'
): string {
  // Blob types carry parameters: `video/mp4;codecs=avc1.42E01E,mp4a.40.2`
  const base = (blob.type || '').split(';')[0].trim().toLowerCase();
  const table = mediaType === 'video' ? VIDEO_EXTENSIONS : PHOTO_EXTENSIONS;
  const fallback = mediaType === 'video' ? 'mp4' : 'jpg';
  return `${stem}.${table[base] ?? fallback}`;
}
